import React, { createContext, useContext, useState, useEffect } from 'react';
import { getCurrentUser, setCurrentUser, removeCurrentUser } from '../utils/auth';
import apiService from '../services/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initAuth = async () => {
      const session = getCurrentUser(); // Returns { id, unique_code } from cookie
      
      if (session && session.id && session.unique_code) {
        try {
          // Fetch fresh user data from server using the ID from cookie
          const userRes = await apiService.getUserById(session.id);
          
          let fullUser = null;
          if (userRes) {
             if (Array.isArray(userRes.data) && userRes.data.length > 0) {
               fullUser = userRes.data[0];
             } else if (userRes.data && typeof userRes.data === 'object') {
               fullUser = userRes.data;
             } else if (!userRes.data && userRes.id) {
                 fullUser = userRes;
             }
          }

          if (fullUser) {
            // SECURITY CHECK: Verify unique_code matches
            // This prevents access if the ID was manipulated or if the session is invalid
            if (String(fullUser.unique_code) !== String(session.unique_code)) {
              console.warn("Security warning: Session unique_code mismatch. Logging out.");
              removeCurrentUser();
              setUser(null);
              setLoading(false);
              return;
            }

            // Remove sensitive data before setting state
            if (fullUser.password) delete fullUser.password;

            // Verify team status with server
            const userTeamResponse = await apiService.getUserTeamRelations({
              user_id: fullUser.id
            });
            
            const hasTeam = userTeamResponse.data && userTeamResponse.data.length > 0;
            const teamId = hasTeam ? userTeamResponse.data[0].team_id : null;
            
            const userWithTeam = {
              ...fullUser,
              hasTeam,
              teamId
            };
             
            setUser(userWithTeam);
          } else {
            // User not found
            removeCurrentUser();
          }
        } catch (err) {
          console.error("Auth sync error:", err);
          removeCurrentUser();
        }
      } else {
        // Invalid session format
        if (session) removeCurrentUser();
      }
      setLoading(false);
    };
    
    initAuth();
  }, []);

  const login = (userData) => {
    // Sanitize data before state
    const safeUser = { ...userData };
    if (safeUser.password) delete safeUser.password;

    setUser(safeUser);
    setCurrentUser(safeUser); // Stores ID and unique_code in cookie
  };

  const logout = () => {
    setUser(null);
    removeCurrentUser();
  };

  const updateUser = (userData) => {
    const safeUser = { ...userData };
    if (safeUser.password) delete safeUser.password;
    
    setUser(safeUser);
    // No need to update cookie as ID and unique_code should remain constant
  };

  const value = {
    user,
    login,
    logout,
    updateUser,
    loading,
    isAuthenticated: !!user,
    isAdmin: user?.is_admin === 1,
    isTeamOwner: user?.is_team_owner === 1
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};